//Helpers
import { convertToUnit } from '../../units_helpers';
import { addDate, dateFormat } from '../../date_helper';
import {
	addLastHourMeasurementDateset,
	getChartData,
	getDeviceChartsConfig,
} from 'src/components/Chart/chart_config';
import { sum } from '../../utilities';
import { deepCloneObj } from 'src/components/Chart/chart_helper';
import CacheStatisticRequest from 'src/components/Chart/chart_cache_helper';
//Types
import type { Device } from 'src/types/Device';
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
	RequestStatisticData,
} from 'src/types/requests/charts/get_stats_period';
import type { ChartConfigInterface, ChartDataInterface } from 'src/types/Chart';
import type { SessionEvents } from './ev_charger_session_info_helpers';

export const getEvChargerStatistics = async (
	device: Device,
	startDate: Date,
	endDate: Date,
): Promise<SessionEvents['statistics']> => {
	const channel = 3200;
	const chartsConfig: ChartConfigInterface = getDeviceChartsConfig(device).find(
		(i) => i.charts?.[0]?.sensorID === channel.toString(),
	);
	const format = 'Y-m-d H:i:00';
	let reqDates: { startDate: Date; endDate: Date }[] = [];
	if (endDate.getTime() - startDate.getTime() < 2 * 60 * 60 * 1000) {
		reqDates = [
			{
				startDate,
				endDate,
			},
		];
	} else {
		const endFirstHourDate = addDate(startDate, 'h', 1, true);
		const startLastHourDate = addDate(endDate, 'h', 0, true);
		reqDates = [
			{
				startDate,
				endDate: addDate(endFirstHourDate, 'i', -1),
			},
			{
				startDate: endFirstHourDate,
				endDate: addDate(startLastHourDate, 'i', -1),
			},
		];
		if (startLastHourDate.getTime() < endDate.getTime()) {
			reqDates.push({
				startDate: startLastHourDate,
				endDate,
			});
		}
	}
	let result: GetStatsResponseData = { history: [] };
	for (let i = 0; i < reqDates.length; i++) {
		const reqData: RequestStatisticData = {
			id: device.id,
			channel,
			date_range: 'custom',
			date_from: dateFormat(reqDates[i].startDate, format),
			date_to: dateFormat(reqDates[i].endDate, format),
		};
		const resData: GetStatsResponseData = await CacheStatisticRequest.getData(
			chartsConfig.url,
			reqData,
			device && device.shared ? device?.server : undefined,
		);
		if (resData?.history?.length) {
			if (reqDates.length === 1) {
				result = resData;
			} else if (resData.interval === 'minute') {
				result = addMinuteMeasurement(result, resData);
			} else {
				result.history = result.history.concat(resData.history);
			}
		}
	}
	const resData: GetStatsResponseData['history'] = (result?.history || []).slice();
	const consumption = sum(resData.map((v) => v?.value ?? 0));
	const chartData: ChartDataInterface = result
		? getChartData(result, chartsConfig?.charts?.[0], 'custom', device)
		: null;

	return {
		chartData,
		consumption: consumption ? convertToUnit(consumption, chartData.units) : null,
	};
};

export const getEventLogStatistics = async (
	device: Device,
	sessionEvent: SessionEvents,
): Promise<SessionEvents['statistics']> => {
	return await getEvChargerStatistics(device, sessionEvent.session_start, sessionEvent.session_end);
};

const addMinuteMeasurement = (
	resData: GetStatsResponseData,
	lastHourRes: GetStatsResponseData,
): GetStatsResponseData => {
	if (!lastHourRes?.history?.length) return resData;
	resData = deepCloneObj(resData);
	const lastHourResHistory: GetStatsResponseHistoryData[] = lastHourRes.history;
	const firstItem = lastHourResHistory[0];
	const date = new Date(firstItem.datetime);
	date.setMinutes(0);
	resData.history = addLastHourMeasurementDateset(resData?.history || [], lastHourResHistory, date);
	return resData;
};
